import { Fragment } from 'react';
import { twMerge } from '@belong/common';
import { ButtonBase, Image, Text } from '@belong/ui';
import clsx from 'clsx';
import { UnitListingDetailsModel } from 'src/api/models';
import { HOUSE_PAGE_STRINGS } from '../../house-page.strings';
import css from './home-features.module.css';
import {
  DisplayFeature,
  getMediaFeatures,
  getNonMediaFeatureNames,
  processHomeFeatures,
} from './utils';

type HomeFeaturesProps = {
  home: UnitListingDetailsModel;
  onFeatureClick: (category: string) => void;
  showMedia?: boolean;
};

export function HomeFeatures({
  home,
  onFeatureClick,
  showMedia = true,
}: HomeFeaturesProps): JSX.Element {
  const categoryToDisplayFeats = processHomeFeatures(home);
  return (
    <div className="w-full">
      {Object.keys(categoryToDisplayFeats).map((category) => (
        <Fragment key={category}>
          <div className="mb-xs">
            <Text fontWeight="semibold">{category}</Text>
          </div>
          {showMedia && (
            <MediaFeatures
              features={getMediaFeatures(categoryToDisplayFeats, category)}
              onFeatureClick={onFeatureClick}
              category={category}
            />
          )}
          <NonMediaFeatures
            featureNames={getNonMediaFeatureNames(
              categoryToDisplayFeats,
              category,
            )}
          />
        </Fragment>
      ))}
    </div>
  );
}

type MediaFeaturesProps = {
  features: DisplayFeature[];
  onFeatureClick: (category: string) => void;
  category: string;
};
function MediaFeatures({
  features,
  onFeatureClick,
  category,
}: MediaFeaturesProps): JSX.Element {
  return (
    <div className="gap-x-lg gap-y-sm grid grid-cols-2 md:grid-cols-4">
      {features.map((displayFeat, idx) => (
        <ButtonBase
          className={clsx('mb-sm w-full')}
          onClick={() => onFeatureClick(category)}
          key={idx}
        >
          <div className="relative">
            {displayFeat.isCommunal && (
              <div className="m-xs rounded-small bg-dark-navy px-xs py-2xs absolute">
                <Text variant="p1" fontWeight="semibold" className="text-white">
                  {HOUSE_PAGE_STRINGS['communal.display']}
                </Text>
              </div>
            )}
            <div
              className={clsx(
                'mb-xs border-navy duration-hover w-full rounded border border-solid transition-all',
                css.image,
              )}
            >
              {displayFeat.media?.[0]?.url && (
                <Image
                  src={displayFeat.media[0].url}
                  alt=""
                  className="rounded object-cover"
                />
              )}
            </div>
            <Text className="text-left">{displayFeat.name}</Text>
          </div>
        </ButtonBase>
      ))}
    </div>
  );
}

type NonMediaFeaturesProps = {
  featureNames: string[];
};
function NonMediaFeatures({
  featureNames,
}: NonMediaFeaturesProps): JSX.Element {
  return (
    <div
      className={twMerge(
        clsx('mb-sm flex w-full flex-wrap', { 'mb-xs': !featureNames?.length }),
      )}
    >
      {featureNames.map((featureName) => (
        <div
          className="mb-xs mr-xs rounded-small border-navy px-xs py-2xs border border-solid"
          key={featureName}
        >
          {featureName}
        </div>
      ))}
    </div>
  );
}
